import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Mail, Shield, LogOut, Building2, Camera, Save, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const API_URL = '';

const SettingsPage = () => {
  const { user, logout, checkAuth } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isAdmin = user?.role === 'admin';

  // Agency settings (admin only)
  const [agencyName, setAgencyName] = useState('Marketing Solutions');
  const [agencyLogo, setAgencyLogo] = useState(null);
  const [savingAgency, setSavingAgency] = useState(false);
  const agencyLogoInput = useRef(null);

  // User profile
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const userPhotoInput = useRef(null);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setPhone(user.phone || '');
      setEmail(user.email || '');
    }
    if (isAdmin) {
      const fetchSettings = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/agency-settings`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) { const d = await res.json(); setAgencyName(d.name || 'Marketing Solutions'); setAgencyLogo(d.logo); }
        } catch {}
      };
      fetchSettings();
    }
  }, [user, isAdmin]);

  const handleSaveAgency = async () => {
    setSavingAgency(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/agency-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: agencyName }),
      });
      toast.success('Configuracion de agencia guardada');
    } catch { toast.error('Error al guardar'); }
    setSavingAgency(false);
  };

  const handleAgencyLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'agency');
    formData.append('entity_id', 'main');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/upload-profile-photo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (res.ok) { const d = await res.json(); setAgencyLogo(d.url); toast.success('Logo actualizado'); }
      else toast.error('Error al subir logo');
    } catch { toast.error('Error'); }
  };

  const handleUserPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'user');
    formData.append('entity_id', user.user_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/upload-profile-photo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (res.ok) { toast.success('Foto de perfil actualizada'); await checkAuth(); }
      else toast.error('Error');
    } catch { toast.error('Error'); }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/account/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone, email }),
      });
      if (res.ok) {
        toast.success('Perfil actualizado');
        await checkAuth();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Error al guardar');
      }
    } catch { toast.error('Error al guardar perfil'); }
    setSavingProfile(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Las contrasenas no coinciden'); return; }
    if (newPassword.length < 4) { toast.error('La contrasena debe tener al menos 4 caracteres'); return; }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/account/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      if (res.ok) {
        toast.success('Contrasena actualizada');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Error al cambiar contrasena');
      }
    } catch { toast.error('Error'); }
    setChangingPassword(false);
  };

  const handleLogout = async () => {
    try { await logout(); window.location.href = '/'; } catch { toast.error('Error al cerrar sesion'); }
  };

  return (
    <DashboardLayout title="Mi Cuenta">
      <div className="space-y-6 max-w-2xl" data-testid="settings-page">
        {/* Profile Card with Photo */}
        <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
          <CardHeader>
            <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}><User size={20} />Mi Perfil</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 mb-6">
              <div className="relative group">
                <Avatar className="h-20 w-20 rounded-full">
                  <AvatarImage src={user?.picture ? (user.picture.startsWith('http') ? user.picture : `${API_URL}${user.picture}`) : undefined} className="rounded-full object-cover" />
                  <AvatarFallback className="bg-orange-600 text-white text-2xl rounded-full">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <button onClick={() => userPhotoInput.current?.click()} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="upload-user-photo">
                  <Camera size={20} className="text-white" />
                </button>
              </div>
              <div className="space-y-1">
                <h3 className={cn('text-xl font-medium', isDark ? 'text-white' : 'text-zinc-900')}>{user?.name}</h3>
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-purple-400" />
                  <Badge className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30">{user?.position || user?.role}</Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><Phone size={14} /> Telefono</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: +52 55 1234 5678" className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')} data-testid="phone-input" />
              </div>
              <div>
                <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><Mail size={14} /> Correo electronico</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@ejemplo.com" className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')} data-testid="email-input" />
              </div>
            </div>
            <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-orange-500 hover:bg-orange-600 text-white mt-4" data-testid="save-profile-btn">
              <Save size={16} className="mr-2" />{savingProfile ? 'Guardando...' : 'Guardar Perfil'}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
          <CardHeader>
            <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}><Lock size={20} />Cambiar Contrasena</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Contrasena actual</Label>
              <div className="relative">
                <Input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white pr-10' : 'bg-zinc-50 border-zinc-200 text-zinc-900 pr-10')} data-testid="current-password-input" />
                <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">{showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Nueva contrasena</Label>
              <div className="relative">
                <Input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white pr-10' : 'bg-zinc-50 border-zinc-200 text-zinc-900 pr-10')} data-testid="new-password-input" />
                <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">{showNew ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Confirmar nueva contrasena</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')} data-testid="confirm-password-input" />
            </div>
            <Button onClick={handleChangePassword} disabled={changingPassword || !currentPassword || !newPassword} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="change-password-btn">
              <Lock size={16} className="mr-2" />{changingPassword ? 'Cambiando...' : 'Cambiar Contrasena'}
            </Button>
          </CardContent>
        </Card>

        {/* Agency Settings - Admin only */}
        {isAdmin && (
          <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
            <CardHeader>
              <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}><Building2 size={20} />Configuracion de Agencia</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <div className="relative group">
                  {agencyLogo ? (
                    <img src={`${API_URL}${agencyLogo}`} alt="Logo" className="w-20 h-20 rounded-full object-cover border-2 border-white/10" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">{agencyName?.charAt(0) || 'M'}</span>
                    </div>
                  )}
                  <button onClick={() => agencyLogoInput.current?.click()} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="upload-agency-logo">
                    <Camera size={20} className="text-white" />
                  </button>
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Nombre de la Agencia</Label>
                    <Input value={agencyName} onChange={e => setAgencyName(e.target.value)} className={cn(isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')} data-testid="agency-name-input" />
                  </div>
                  <Button onClick={handleSaveAgency} disabled={savingAgency} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-agency-btn">
                    <Save size={16} className="mr-2" />{savingAgency ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logout */}
        <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div><h3 className={cn('font-medium', isDark ? 'text-white' : 'text-zinc-900')}>Cerrar Sesion</h3><p className={cn('text-sm', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Cierra tu sesion de la plataforma</p></div>
              <Button variant="outline" onClick={handleLogout} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="logout-settings-btn"><LogOut size={18} className="mr-2" />Cerrar Sesion</Button>
            </div>
          </CardContent>
        </Card>

        {/* Hidden inputs */}
        <input ref={agencyLogoInput} type="file" accept="image/*" className="hidden" onChange={handleAgencyLogoUpload} />
        <input ref={userPhotoInput} type="file" accept="image/*" className="hidden" onChange={handleUserPhotoUpload} />
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
